import { captureException } from '@sentry/browser'

import {
    resetPasswordFailure,
    resetPasswordRequest,
    sendResetEmailFailure,
    sendResetEmailRequest,
    sendResetEmailSuccess,
    signUpFailure,
    signUpRequest,
    signUpSuccess,
    verifyEmailFailure,
    verifyEmailRequest,
} from 'actions/auth'
import {
    confirmSignUp,
    resetPassword as resetPasswordApi,
    sendResetEmail as sendResetEmailApi,
    signUp as signUpApi,
} from 'api/Cerebro/unscoped/auth'
import { SegmentEventName } from 'const/monitoring/segment'
import { formatSignUpData, SignupData } from 'helpers/params'
import { getPath } from 'routing/pagePaths'
import { navigate } from 'routing/RoutingService'
import { selectDomainValue as selectAuthDomainValue } from 'selectors/auth'

import { sendTrackEvent } from '../thirdParty'

import { dispatch, getReduxState } from './reduxBridge'
import { signIn } from './signIn'

export async function signUp(formValues: SignupData): Promise<void> {
    dispatch(signUpRequest(formValues))

    const { email, password, token } = formValues
    const signUpData = formatSignUpData(formValues)

    try {
        await signUpApi(signUpData)
        dispatch(signUpSuccess({ email, password }))
        sendTrackEvent(SegmentEventName.SIGN_UP_SUCCESS)
        if (token) {
            await signIn({ email, password })
        } else {
            navigate(getPath('authVerifyEmailPage'))
        }
    } catch (err: any) {
        captureException(err)
        sendTrackEvent(SegmentEventName.SIGN_UP_ERROR)
        dispatch(signUpFailure({ error: err.message }))
    }
}

export async function verifyEmail({ code }: { code: string }): Promise<void> {
    dispatch(verifyEmailRequest({ code }))

    try {
        const email: string = selectAuthDomainValue(getReduxState(), 'email')
        const password: string = selectAuthDomainValue(
            getReduxState(),
            'password'
        )

        await confirmSignUp(email, code)

        await signIn({ email, password })
        sendTrackEvent(SegmentEventName.SIGN_UP_VERIFY_EMAIL_SUCCESS)
    } catch (err: any) {
        dispatch(verifyEmailFailure({ error: err.message }))
        sendTrackEvent(SegmentEventName.SIGN_UP_VERIFY_EMAIL_ERROR)
    }
}

export async function sendResetEmail({
    email,
}: {
    email: string
}): Promise<void> {
    dispatch(sendResetEmailRequest({ email }))

    try {
        await sendResetEmailApi(email)

        dispatch(sendResetEmailSuccess())

        navigate(getPath('authResetPasswordPage'))
    } catch (err: any) {
        dispatch(sendResetEmailFailure({ error: err.message }))
    }
}

export async function resetPassword({
    email,
    code,
    newPassword,
}: {
    email: string
    code: string
    newPassword: string
}): Promise<void> {
    dispatch(resetPasswordRequest({ email, code, newPassword }))

    try {
        await resetPasswordApi(email, code, newPassword)

        await signIn({ email, password: newPassword })
    } catch (err: any) {
        dispatch(resetPasswordFailure({ error: err.message }))
    }
}
